import React, { useEffect, useRef, useCallback } from 'react'
import {
  Building2, ExternalLink, FileText, Tag,
} from 'lucide-react'
import Modal from '../ui/Modal'
import Badge from '../ui/Badge'
import AwardAmountsChart, {
  type ContractDetail,
  fmtDate,
  usaspendingUrl,
} from './AwardAmountsChart'
import { trackEvent } from '../../services/tracking'

export type { ContractDetail }

interface ContractDetailModalProps {
  isOpen: boolean
  onClose: () => void
  contract: ContractDetail
}

const ContractDetailModal: React.FC<ContractDetailModalProps> = ({
  isOpen,
  onClose,
  contract,
}) => {
  const openedAt = useRef(performance.now())

  useEffect(() => {
    if (isOpen) {
      openedAt.current = performance.now()
      trackEvent('view_opened', {
        view: 'contract_detail',
        contract_id: contract.id,
        contract_number: contract.contract_number,
      })
    }
  }, [isOpen, contract.id, contract.contract_number])

  const trackedClose = useCallback(() => {
    trackEvent('view_closed', {
      view: 'contract_detail',
      contract_id: contract.id,
      duration_ms: Math.round(performance.now() - openedAt.current),
    })
    onClose()
  }, [onClose, contract.id])
  return (
    <Modal isOpen={isOpen} onClose={trackedClose} title={
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-blue-500" />
        <span>Contract Detail</span>
        {contract.type_description && (
          <Badge variant="default" className="text-[10px]">{contract.type_description}</Badge>
        )}
      </div>
    } size="xl">
      <div className="max-h-[70vh] overflow-y-auto space-y-6 -mx-6 px-6">
        {/* Contract number & title */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Contract Number</p>
              <p className="text-sm font-mono font-semibold text-foreground">{contract.contract_number}</p>
            </div>
            {contract.contract_type && (
              <Badge variant="default" className="text-[10px]">{contract.contract_type}</Badge>
            )}
          </div>
          {contract.title && (
            <div>
              <p className="text-xs text-muted-foreground">Title</p>
              <p className="text-sm text-foreground">{contract.title}</p>
            </div>
          )}
          {contract.description && (
            <div>
              <p className="text-xs text-muted-foreground">Description</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{contract.description}</p>
            </div>
          )}

          {/* Dates grid */}
          <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Award Date</p>
              <p className="text-sm font-medium text-foreground">{fmtDate(contract.award_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Start Date</p>
              <p className="text-sm font-medium text-foreground">{fmtDate(contract.start_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">End Date</p>
              <p className="text-sm font-medium text-foreground">
                {fmtDate(contract.period_of_performance_current_end_date || contract.end_date)}
              </p>
            </div>
          </div>

          {usaspendingUrl(contract) && (
            <a
              href={usaspendingUrl(contract)!}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            >
              View on USAspending
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Award Amounts Visualization */}
        <AwardAmountsChart contract={contract} />

        {/* Awarding Agency Section */}
        {(contract.awarding_agency || contract.funding_agency_name) && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Awarding Agency
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {contract.awarding_agency && (
                <div>
                  <p className="text-xs text-muted-foreground">Agency</p>
                  <p className="text-sm font-medium text-foreground">{contract.awarding_agency}</p>
                </div>
              )}
              {contract.awarding_office && (
                <div>
                  <p className="text-xs text-muted-foreground">Office</p>
                  <p className="text-sm font-medium text-foreground">{contract.awarding_office}</p>
                </div>
              )}
              {contract.funding_agency_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Funding Agency</p>
                  <p className="text-sm font-medium text-foreground">{contract.funding_agency_name}</p>
                </div>
              )}
              {contract.funding_office && (
                <div>
                  <p className="text-xs text-muted-foreground">Funding Office</p>
                  <p className="text-sm font-medium text-foreground">{contract.funding_office}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product name (from ContractProductMatch) */}
        {contract.product_name && (
          <div className="flex items-center gap-2 text-sm">
            <Tag className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Matched product:</span>
            <span className="font-medium text-foreground">{contract.product_name}</span>
          </div>
        )}

        {/* Performance notes */}
        {contract.performance_notes && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">Performance Notes</h4>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-foreground leading-relaxed">{contract.performance_notes}</p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ContractDetailModal
